<template>
  <div class="dashboard-container p-4">
    <div class="bg-white p-5 rounded-lg shadow mb-4">
      <h1 class="text-2xl font-bold text-gray-800">
        Workshop Dashboard
      </h1>
      <p class="text-gray-600">
        Manage repairs, maintenance, and equipment checks.
      </p>
    </div>
        
    <!-- Status Overview Cards -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
      <Card class="shadow-sm">
        <template #content>
          <div class="flex flex-col items-center p-4">
            <div class="text-2xl font-bold text-red-600 mb-2">
              12
            </div>
            <div class="text-sm text-gray-600">
              Urgent Repairs
            </div>
            <div class="mt-2 text-red-500 text-sm font-medium">
              Due Today
            </div>
          </div>
        </template>
      </Card>
            
      <Card class="shadow-sm">
        <template #content>
          <div class="flex flex-col items-center p-4">
            <div class="text-2xl font-bold text-orange-600 mb-2">
              24
            </div>
            <div class="text-sm text-gray-600">
              Pending Repairs
            </div>
            <div class="mt-2 text-orange-500 text-sm font-medium">
              This Week
            </div>
          </div>
        </template>
      </Card>
            
      <Card class="shadow-sm">
        <template #content>
          <div class="flex flex-col items-center p-4">
            <div class="text-2xl font-bold text-blue-600 mb-2">
              18
            </div>
            <div class="text-sm text-gray-600">
              Equipment Checks
            </div>
            <div class="mt-2 text-blue-500 text-sm font-medium">
              Scheduled
            </div>
          </div>
        </template>
      </Card>
            
      <Card class="shadow-sm">
        <template #content>
          <div class="flex flex-col items-center p-4">
            <div class="text-2xl font-bold text-green-600 mb-2">
              35
            </div>
            <div class="text-sm text-gray-600">
              Completed Tasks
            </div>
            <div class="mt-2 text-green-500 text-sm font-medium">
              This Month
            </div>
          </div>
        </template>
      </Card>
    </div>
        
    <!-- Quick Actions Buttons -->
    <div class="flex flex-wrap gap-2 mb-4">
      <Button
        label="New Repair Ticket"
        icon="pi pi-wrench"
        severity="secondary"
      />
      <Button
        label="Schedule Maintenance"
        icon="pi pi-calendar"
        severity="secondary"
      />
      <Button
        label="Equipment Check"
        icon="pi pi-check-square"
        severity="secondary"
      />
      <Button
        label="Order Parts"
        icon="pi pi-shopping-cart"
        severity="secondary"
      />
    </div>
        
    <!-- Repair Queue -->
    <div class="bg-white p-5 rounded-lg shadow mb-4">
      <h2 class="text-xl font-semibold mb-4">
        Today's Repair Queue
      </h2>
      <DataTable
        :value="repairQueue"
        :rows="5"
        striped-rows
        responsive-layout="scroll"
      >
        <Column
          field="id"
          header="ID"
          style="width: 5%"
        />
        <Column
          field="equipment"
          header="Equipment"
          style="width: 25%"
        />
        <Column
          field="issue"
          header="Issue"
          style="width: 25%"
        />
        <Column
          field="priority"
          header="Priority"
          style="width: 10%"
        >
          <template #body="slotProps">
            <Tag
              :value="slotProps.data.priority"
              :severity="getPrioritySeverity(slotProps.data.priority)"
            />
          </template>
        </Column>
        <Column
          field="assignee"
          header="Assigned To"
          style="width: 15%"
        />
        <Column
          field="actions"
          header="Actions"
          style="width: 15%"
        >
          <template #body>
            <Button
              icon="pi pi-check"
              text
              rounded
              aria-label="Complete"
              class="mr-2"
            />
            <Button
              icon="pi pi-info-circle"
              text
              rounded
              aria-label="Details"
            />
          </template>
        </Column>
      </DataTable>
    </div>
        
    <!-- Upcoming Maintenance -->
    <div class="bg-white p-5 rounded-lg shadow">
      <h2 class="text-xl font-semibold mb-4">
        Upcoming Maintenance
      </h2>
      <Timeline
        :value="maintenanceSchedule"
        align="alternate"
        class="mt-3"
      >
        <template #content="slotProps">
          <Card class="mb-0">
            <template #content>
              <div class="flex flex-col">
                <span class="font-bold text-lg mb-1">{{ slotProps.item.equipment }}</span>
                <span class="text-gray-700 mb-2">{{ slotProps.item.task }}</span>
                <div class="flex items-center gap-2">
                  <i class="pi pi-calendar text-sm" />
                  <span class="text-sm text-gray-600">{{ slotProps.item.date }}</span>
                </div>
                <div class="mt-2">
                  <Tag
                    :value="slotProps.item.status"
                    :severity="getStatusSeverity(slotProps.item.status)"
                  />
                </div>
              </div>
            </template>
          </Card>
        </template>
        <template #marker="slotProps">
          <span
            class="custom-marker shadow-1"
            :style="{backgroundColor: getMarkerColor(slotProps.item.status)}"
          >
            <i class="pi pi-cog" />
          </span>
        </template>
      </Timeline>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';

// Sample repair queue
const repairQueue = ref([
    { 
        id: 'RP-1024', 
        equipment: 'ARRI Alexa LF Camera', 
        issue: 'Lens mount not securing properly',
        priority: 'High',
        assignee: 'John Doe'
    },
    { 
        id: 'RP-1023', 
        equipment: 'Sennheiser Wireless Mic Set', 
        issue: 'Intermittent signal loss',
        priority: 'Medium',
        assignee: 'Sarah Johnson'
    },
    { 
        id: 'RP-1022', 
        equipment: 'LED Panel 1000W', 
        issue: 'Power supply failure',
        priority: 'High',
        assignee: 'Mike Smith'
    },
    { 
        id: 'RP-1021', 
        equipment: 'DJI Ronin Gimbal', 
        issue: 'Motor calibration needed',
        priority: 'Low',
        assignee: 'Emma Davis'
    },
    { 
        id: 'RP-1020', 
        equipment: 'Blackmagic ATEM Switcher', 
        issue: 'HDMI port not responding',
        priority: 'Medium',
        assignee: 'John Doe'
    }
]);

// Sample maintenance schedule
const maintenanceSchedule = ref([
    {
        equipment: 'Sound Equipment',
        task: 'Quarterly inspection and calibration',
        date: 'May 20, 2023',
        status: 'Scheduled'
    },
    {
        equipment: 'Camera Gear',
        task: 'Monthly cleaning and firmware updates',
        date: 'May 22, 2023',
        status: 'Pending'
    },
    {
        equipment: 'Lighting Rigs',
        task: 'Safety inspection and bulb replacement',
        date: 'May 25, 2023',
        status: 'In Progress'
    },
    {
        equipment: 'Production Vehicles',
        task: 'Regular maintenance and servicing',
        date: 'May 28, 2023',
        status: 'Scheduled'
    }
]);

// Function to determine priority severity for tags
const getPrioritySeverity = (priority) => {
    switch (priority.toLowerCase()) {
        case 'high':
            return 'danger';
        case 'medium':
            return 'warning';
        case 'low':
            return 'info';
        default:
            return 'secondary';
    }
};

// Function to determine status severity for tags
const getStatusSeverity = (status) => {
    switch (status.toLowerCase()) {
        case 'completed':
            return 'success';
        case 'in progress':
            return 'info';
        case 'pending':
            return 'warning';
        case 'scheduled':
            return 'secondary';
        default:
            return 'secondary';
    }
};

// Function to get marker color based on status
const getMarkerColor = (status) => {
    switch (status.toLowerCase()) {
        case 'completed':
            return '#22c55e';
        case 'in progress':
            return '#3b82f6';
        case 'pending':
            return '#f59e0b';
        case 'scheduled':
            return '#64748b';
        default:
            return '#64748b';
    }
};
</script>

<style scoped>
.dashboard-container {
    background-color: #f5f7fa;
    min-height: 100vh;
}

.custom-marker {
    display: flex;
    width: 2rem;
    height: 2rem;
    align-items: center;
    justify-content: center;
    color: #ffffff;
    border-radius: 50%;
    z-index: 1;
}
</style> 